import React, { useState, useEffect } from 'react';
import { SelectButton } from 'primereact/selectbutton';
import { CircleMinus, CirclePlus, SquareMinus, SquarePlus } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';

import {
  FiltersOperators,
  FiltersOperatorsForDate,
  NumberFiltersOperators,
  monthWeekFilterOptions,
  timeFilterOptions,
} from '../../../../constants';
import { getDataByCube } from '../service/widget.service';
import { convertDateToLocaleString } from '../../../../utils/Helpers';
import '../../Dashboard/css/dashboard.styles.css';

const CONDITIONS = [
  { key: 'and', value: 'All of these conditions are true' },
  { key: 'or', value: 'Any of these conditions are true' },
];

const shouldUseValueDropdown = columnType =>
  ['string', 'boolean'].includes(columnType);

const getDimensionColumnType = dimension => {
  if (!dimension) return 'string';
  if (dimension.type) return dimension.type;
  if (dimension.meta?.format) return 'number';
  return 'string';
};

const resetFilterFieldValues = innerRecord => {
  innerRecord.operator = '';
  innerRecord.value = '';
  delete innerRecord.operatorType;
  delete innerRecord.isTimeFilter;
  delete innerRecord.dateFilterOperatorType;
  delete innerRecord.to_date_value;
};

const getOperatorOptionsByColumnType = columnType => {
  if (columnType === 'boolean') return [FiltersOperators[0]];
  if (columnType === 'string') {
    return FiltersOperators.filter(operator => operator.type !== 'time');
  }
  if (columnType === 'number') return NumberFiltersOperators;
  if (columnType === 'time') return timeFilterOptions;
  return FiltersOperators;
};

const GetConditionRow = ({
  dimensionList,
  action,
  innerRecord,
  widgetFormik,
  filterConditions,
  index,
  record,
  filterFor,
  isCopyUse,
  rowKey,
}) => {
  const [dataList, setDataList] = useState([]);

  const selectedDimension = (dimensionList || []).find(
    item => item.name === innerRecord.tableField
  );
  const columnType = getDimensionColumnType(selectedDimension);

  useEffect(() => {
    const tableField = innerRecord.tableField;

    if (!tableField) {
      setDataList([]);
      return;
    }

    const dimension = (dimensionList || []).find(
      item => item.name === tableField
    );
    const type = getDimensionColumnType(dimension);

    if (!shouldUseValueDropdown(type) || !dimension?.name) {
      setDataList([]);
      return;
    }

    let isCancelled = false;

    const fetchValueOptions = async () => {
      try {
        const [cubeName, dimensionName] = dimension.name.split('.');
        const response = await getDataByCube(cubeName, dimensionName);
        if (!isCancelled) {
          setDataList(
            Array.isArray(response)
              ? response.map(item => ({ key: item.value, value: item.label }))
              : []
          );
        }
      } catch {
        if (!isCancelled) {
          setDataList([]);
        }
      }
    };

    fetchValueOptions();

    return () => {
      isCancelled = true;
    };
  }, [innerRecord.tableField, isCopyUse, dimensionList.length]);

  useEffect(() => {
    if (columnType === 'time' && !innerRecord.dateFilterOperatorType) {
      innerRecord.dateFilterOperatorType = 'equals';
    }
  }, [columnType]);

  return (
    <div className="grid w-full filter-section">
      <div className="col-12 md:col-3">
        <span className="p-float-label">
          <Dropdown
            id="field"
            showClear
            filter
            key={innerRecord.tableField}
            options={dimensionList}
            disabled={action === 'view' ? true : false}
            value={
              dimensionList?.find(a => a.name === innerRecord.tableField)
                ?.value || ''
            }
            optionLabel="value"
            className="w-full"
            onChange={async event => {
              if (!event.value) {
                innerRecord.tableField = '';
                resetFilterFieldValues(innerRecord);
                setDataList([]);
                await widgetFormik.setFieldValue(filterFor, filterConditions);
                return;
              }

              const fullObject = dimensionList.find(
                item => item.value === event.value
              );

              innerRecord.tableField = fullObject?.name || '';
              resetFilterFieldValues(innerRecord);
              setDataList([]);
              await widgetFormik.setFieldValue(filterFor, filterConditions);
            }}
          />
          <label htmlFor="field">
            Field <span className="text-red-400">*</span>
          </label>
        </span>
      </div>
      {columnType === 'time' ? (
        <div className="col-12 md:col-3">
          <span className="p-float-label">
            <Dropdown
              showClear
              filter
              id="field"
              key={innerRecord.tableField}
              options={FiltersOperatorsForDate}
              disabled={action === 'view' ? true : false}
              value={
                FiltersOperatorsForDate?.find(
                  a => a.key === innerRecord.dateFilterOperatorType
                )?.value || {}
              }
              optionLabel="value"
              className="w-full"
              onChange={async event => {
                const selectedLabel = event.value;

                const fullObject = FiltersOperatorsForDate.find(
                  item => item.value === selectedLabel
                );

                innerRecord.dateFilterOperatorType = fullObject?.key || '';
                await widgetFormik.setFieldValue(filterFor, filterConditions);
              }}
            />
            <label htmlFor="field">
              Select Operator <span className="text-red-400">*</span>
            </label>
          </span>
        </div>
      ) : null}
      <div className="col-12 md:col-3">
        <span className="p-float-label">
          <Dropdown
            showClear
            filter
            key={innerRecord.operator}
            className="w-full"
            optionLabel="value"
            options={getOperatorOptionsByColumnType(columnType)}
            disabled={action === 'view'}
            value={
              FiltersOperators?.find(a => a.key === innerRecord.operator)
                ?.value ||
              NumberFiltersOperators?.find(a => a.key === innerRecord.operator)
                ?.value ||
              timeFilterOptions?.find(a => a.key === innerRecord.operator)
                ?.value
            }
            onChange={async event => {
              const selectedLabel = event.value;
              const options = getOperatorOptionsByColumnType(columnType);
              const fullObject = options.find(
                item => item.value === selectedLabel
              );
              innerRecord.operator = fullObject?.key || '';
              if (fullObject?.custom) {
                innerRecord.operatorType = fullObject.custom;
              } else {
                innerRecord.operatorType = '';
              }
              if (columnType === 'time') {
                innerRecord.isTimeFilter = true;
              } else {
                innerRecord.isTimeFilter = false;
              }

              await widgetFormik.setFieldValue(filterFor, filterConditions);
            }}
          />
          <label htmlFor={innerRecord.operator}>
            Select Operator <span className="text-red-400">*</span>
          </label>
        </span>
      </div>
      {shouldUseValueDropdown(columnType) &&
      dataList.length &&
      !['set', 'notSet'].includes(innerRecord.operator) ? (
        <div className="col-12 md:col-3">
          <span className="p-float-label">
            <Dropdown
              key={`${rowKey}-value-dropdown`}
              showClear
              filter
              options={dataList}
              disabled={action === 'view'}
              value={dataList?.find(a => a.key === innerRecord.value)?.value}
              optionLabel="value"
              className="w-full"
              onChange={async event => {
                const selectedLabel = event.value;

                const fullObject = dataList.find(
                  item => item.value === selectedLabel
                );

                innerRecord.value = fullObject?.key || '';
                await widgetFormik.setFieldValue(filterFor, filterConditions);
              }}
            />
            <label htmlFor="field">
              Select Value <span className="text-red-400">*</span>
            </label>
          </span>
        </div>
      ) : columnType === 'time' ? (
        innerRecord.operatorType === 'daysField' ? (
          <div className="col-12 md:col-3">
            <span className="p-float-label">
              <InputText
                className="w-full"
                disabled={
                  !innerRecord.operatorType ||
                  !innerRecord.operatorType === 'daysField'
                }
                onChange={event => {
                  innerRecord.value = event.target.value;
                  widgetFormik.setFieldValue(filterFor, filterConditions);
                }}
                value={innerRecord.value}
              />
              <label htmlFor="field">
                Days <span className="text-red-400">*</span>
              </label>
            </span>
          </div>
        ) : innerRecord.operatorType === 'monthWeekField' ? (
          <div className="col-12 md:col-3">
            <span className="p-float-label">
              <Dropdown
                key={`${rowKey}-month-week-dropdown`}
                showClear
                filter
                options={monthWeekFilterOptions}
                disabled={action === 'view'}
                value={
                  monthWeekFilterOptions?.find(a => a.key === innerRecord.value)
                    ?.value
                }
                optionLabel="value"
                className="w-full"
                onChange={async event => {
                  const selectedLabel = event.value;
                  const fullObject = monthWeekFilterOptions.find(
                    item => item.value === selectedLabel
                  );
                  innerRecord.value = fullObject?.key || '';
                  await widgetFormik.setFieldValue(filterFor, filterConditions);
                }}
              />
              <label htmlFor="field">Select Week</label>
            </span>
          </div>
        ) : innerRecord.operatorType === 'datePicker' ? (
          <>
            <div className="col-12 md:col-3">
              <span className="p-float-label">
                <Calendar
                  id="chooseDate"
                  value={
                    innerRecord.value &&
                    new Date(convertDateToLocaleString(innerRecord.value))
                  }
                  onChange={e => {
                    innerRecord.value = moment(e.target.value).format(
                      'YYYY-MM-DD'
                    );

                    widgetFormik.setFieldValue(filterFor, filterConditions);
                  }}
                  disabled={action === 'view'}
                  className="w-full"
                />
                <label htmlFor="chooseDate" className="text-xs">
                  {innerRecord.operator === 'inDateRange'
                    ? 'Choose From Date'
                    : 'Choose Date'}{' '}
                  <span className="text-red-400">*</span>
                </label>
              </span>
            </div>

            {innerRecord.operator === 'inDateRange' && (
              <div className="col-12 md:col-3">
                <span className="p-float-label">
                  <Calendar
                    id="chooseDate"
                    value={
                      innerRecord.to_date_value &&
                      new Date(
                        convertDateToLocaleString(innerRecord.to_date_value)
                      )
                    }
                    onChange={e => {
                      innerRecord.to_date_value = moment(e.target.value).format(
                        'YYYY-MM-DD'
                      );
                      widgetFormik.setFieldValue(filterFor, filterConditions);
                    }}
                    disabled={action === 'view'}
                    className="w-full"
                    minDate={new Date(innerRecord.value)}
                  />
                  <label htmlFor="chooseDate" className="text-xs">
                    Choose To Date
                    <span className="text-red-400">*</span>
                  </label>
                </span>
              </div>
            )}
          </>
        ) : null
      ) : (
        !['set', 'notSet'].includes(innerRecord.operator) && (
          <div className="col-12 md:col-3">
            <span className="p-float-label">
              <InputText
                key={`${rowKey}-value-input`}
                className="w-full"
                disabled={action === 'view'}
                onChange={event => {
                  innerRecord.value = event.target.value;
                  widgetFormik.setFieldValue(filterFor, filterConditions);
                }}
                value={innerRecord.value || ''}
              />
              <label htmlFor="field">
                Value <span className="text-red-400">*</span>
              </label>
            </span>
          </div>
        )
      )}
      <div className="col-2 md:col-1 flex justify-content-center gap-2 align-items-center">
        {action !== 'view' && (
          <>
            <SquarePlus
              className="text-primary text-sm w-1rem h-1rem"
              onClick={() => {
                record.fields.push({
                  tableField: '',
                  operator: '',
                  value: '',
                });
                widgetFormik.setFieldValue(filterFor, filterConditions);
              }}
            />
            {record.fields?.length > 1 ? (
              <SquareMinus
                className="text-primary text-sm w-1rem h-1rem"
                fontSize="large"
                onClick={() => {
                  record.fields.splice(index, 1);
                  widgetFormik.setFieldValue(filterFor, filterConditions);
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

const FilterSection = ({
  widgetFormik,
  filterConditions,
  action,
  dimensionList,
  filterFor,
  isCopyUse,
}) => {
  if (!filterConditions) return null;
  return (
    <>
      <SelectButton
        value={filterConditions.condition}
        pt={{
          button: { className: 'p-2 border-round-sm md:border-noround' },
          label: { className: 'text-xs' },
        }}
        optionLabel="value"
        optionValue="key"
        onChange={event => {
          filterConditions.condition = event.target.value;
          widgetFormik.setFieldValue(filterFor, filterConditions);
        }}
        options={CONDITIONS}
      />

      <div className="grid p-2 w-full  surface-card  border-1 border-solid border-round border-200 grid-nogutter">
        {filterConditions.filters.map((record, conditionIndex) => {
          return (
            <div className="grid grid-nogutter w-full" key={conditionIndex}>
              {record && record?.fields?.length > 0 ? (
                <>
                  <div className="p-2 col-11 grid grid-nogutter gap-1 my-2 surface-100  border-1 border-solid border-round border-200">
                    <div className="grid grid-nogutter w-full">
                      {record && record?.fields?.length > 1 ? (
                        <SelectButton
                          value={record.condition}
                          pt={{
                            button: {
                              className:
                                'p-2 border-round-sm md:border-noround',
                            },
                            label: { className: 'text-xs' },
                            root: { className: 'mb-2' },
                          }}
                          optionLabel="value"
                          optionValue="key"
                          onChange={event => {
                            record.condition = event.target.value;
                            widgetFormik.setFieldValue(
                              filterFor,
                              filterConditions
                            );
                          }}
                          disabled={action === 'view'}
                          options={CONDITIONS}
                        />
                      ) : null}
                    </div>
                    {record.fields.map((innerRecord, index) => {
                      return (
                        <GetConditionRow
                          key={`${conditionIndex}-${index}-${innerRecord.tableField || 'empty'}`}
                          rowKey={`${conditionIndex}-${index}-${innerRecord.tableField || 'empty'}`}
                          dimensionList={dimensionList}
                          action={action}
                          innerRecord={innerRecord}
                          widgetFormik={widgetFormik}
                          filterConditions={filterConditions}
                          index={index}
                          record={record}
                          filterFor={filterFor}
                          isCopyUse={isCopyUse}
                        />
                      );
                    })}
                  </div>

                  <div className="col-1 flex justify-content-center align-items-center">
                    {action !== 'view' && (
                      <>
                        {conditionIndex === 0 ? (
                          <CirclePlus
                            className="text-primary text-sm w-1rem h-1rem"
                            onClick={() => {
                              filterConditions.filters.push({
                                condition: 'and',
                                fields: [
                                  { tableField: '', operator: '', value: '' },
                                ],
                              });
                              widgetFormik.setFieldValue(
                                filterFor,
                                filterConditions
                              );
                            }}
                          />
                        ) : (
                          filterConditions.filters.length > 1 && (
                            <CircleMinus
                              className="text-primary text-sm w-1rem h-1rem"
                              fontSize="large"
                              onClick={() => {
                                filterConditions.filters.splice(
                                  conditionIndex,
                                  1
                                );
                                widgetFormik.setFieldValue(
                                  filterFor,
                                  filterConditions
                                );
                              }}
                            />
                          )
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default FilterSection;
